import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import { useMoiaSuccessNotification } from '@backoffice-frontend/patterns'
import {
  SESSION_PROLONG_BY_IN_MINUTES,
  useTemporarySessionId,
} from '@backoffice-frontend/temporary-backoffice-sessions-common'
import { useTranslation } from 'react-i18next'
import {
  useProlongTemporarySessionMutation,
  useTemporarySessionBannerQuery,
} from './TemporarySessionBanner.hook'
import type { ActiveTemporarySession } from './types'
import { resolveOrganizationName } from './utils'

type UseActiveTemporarySession = {
  session: ActiveTemporarySession | undefined
  loading: boolean
  /** Resolved name of the organization a scoped-access session targets. */
  organizationName: string | undefined
  prolong: () => Promise<void>
  isProlonging: boolean
}

/**
 * The actor's own active temporary Backoffice session, if any, plus the actions
 * that can be taken on it.
 */
export const useActiveTemporarySession = (): UseActiveTemporarySession => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const temporarySessionId = useTemporarySessionId()

  const { data, loading } = useErrorHandlingQuery(
    useTemporarySessionBannerQuery,
    {
      skip: !temporarySessionId,
      // The banner renders on every route, so a transient failure here must not
      // put an error toast in front of the actor on every page they open.
      onError: error => {
        console.error('Could not load the temporary session banner:', error)
      },
    },
  )

  const [prolongSession, { loading: isProlonging }] = useErrorHandlingMutation(
    useProlongTemporarySessionMutation,
  )

  // An expired-but-unclosed session still renders: the actor has to be told it
  // ran out, which is what the banner's expired state is for. A closed one does
  // not, which is why the query returns only a non-closed session.
  const session = data?.currentTemporaryBackofficeSession ?? undefined

  return {
    session,
    loading,
    organizationName: resolveOrganizationName(session),
    prolong: async () => {
      if (!temporarySessionId) {
        return
      }

      const result = await prolongSession({
        variables: { input: { sessionId: temporarySessionId } },
      })

      if (result.data?.prolongTemporaryBackofficeSession) {
        enqueueMoiaSuccessNotification(
          t('authenticatedAppView_temporarySession_prolonged_notification', {
            minutes: SESSION_PROLONG_BY_IN_MINUTES,
          }),
        )
      }
    },
    isProlonging,
  }
}
